import { Wallet, Smartphone, Wrench, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import { GUIDE_CHARACTER } from "@/assets/guide";
import { articles } from "@/mocks/carriers";
import { mobilePlans, type MobilePlan } from "@/data/plans.generated";

const worries = [
  { icon: "ri-money-cny-circle-line", title: "毎月のスマホ代が高い", desc: "請求を4項目に分けてから、使い方に合う料金プランを探します。", to: "/bill-check" },
  { icon: "ri-smartphone-line", title: "iPhoneをどこで買うか迷う", desc: "iPhone 18 Proを例に、購入先・MNP・2年返却を総額で整理します。", to: "/articles/iphone-18-pro-mnp" },
  { icon: "ri-tools-line", title: "修理か買い替えか迷う", desc: "保証、修理見積もり、使用年数から判断する順番を確認できます。", to: "/articles/iphone-repair-or-replace" },
  { icon: "ri-refresh-line", title: "乗り換えで失敗したくない", desc: "請求・使用量・通話・家族割など、申し込み前の確認事項をまとめています。", to: "/articles/before-switching" },
];

const billChecks = [
  { no: "01", title: "基本プランの容量", desc: "実際に使っているデータ量と契約容量が合っているか。使い方に対して容量が多すぎないか確認します。" },
  { no: "02", title: "有料オプション", desc: "契約時に付いた留守電、故障保証、動画サービスを一覧で確認。" },
  { no: "03", title: "端末代金の残債", desc: "分割払いの残り回数と、返却プログラムの条件を明細で確認。" },
  { no: "04", title: "通話料金の内訳", desc: "定額オプションと従量課金分を分け、通話パターンに合っているか判断。" },
];

const planById = (id: string) => mobilePlans.find((plan) => plan.id === id) as MobilePlan | undefined;

const carrierBrandColors: Record<string, string> = {
  "ドコモ": "#CC0033",
  "au": "#EB5505",
  "ソフトバンク": "#1F2937",
  "楽天モバイル": "#BF0000",
  "UQ mobile": "#6A1684",
  "Y!mobile": "#E60012",
  "ahamo": "#1F2937",
  "povo": "#86A900",
  "LINEMO": "#00A8A8",
  "IIJmio": "#164A9B",
  "日本通信SIM": "#E05A24",
  "mineo": "#7EBE24",
};

const comparisonCapacityColumns = [
  { id: "small", label: "3〜6GB", note: "連絡・検索中心" },
  { id: "medium", label: "7〜12GB", note: "SNSも使う" },
  { id: "twenty", label: "15〜20GB", note: "動画も見る" },
  { id: "thirty", label: "25〜45GB", note: "外でもよく使う" },
  { id: "large", label: "50GB〜", note: "大容量・無制限" },
] as const;

const comparisonMatrix = [
  { brand: "ドコモ", type: "大手", plans: { small: "docomo_mini_4gb", medium: "docomo_mini_10gb", twenty: "docomo_poikatsu_20_20gb", large: "docomo_max_unlimited" } },
  { brand: "au", type: "大手", plans: { small: "au_smartmini_plus_5gb", large: "au_unlimited_max_plus" } },
  { brand: "ソフトバンク", type: "大手", plans: { small: "sb_minifit2_5gb", large: "sb_teigaku_unlimited_unlimited" } },
  { brand: "楽天モバイル", type: "自社回線", plans: { small: "rakuten_3gb", twenty: "rakuten_20gb", large: "rakuten_unlimited" } },
  { brand: "UQ mobile", type: "サブブランド", plans: { small: "uq_tokutoku2_under5", thirty: ["uq_tokutoku2_30gb", "uq_komikomi_value_35gb"] } },
  { brand: "Y!mobile", type: "サブブランド", plans: { small: "ymobile_simple3_s", thirty: ["ymobile_simple3_m", "ymobile_simple3_l"] } },
  { brand: "ahamo", type: "オンライン", plans: { thirty: "ahamo_30gb", large: "ahamo_110gb" } },
  { brand: "LINEMO", type: "オンライン", plans: { small: "linemo_best_3gb", medium: "linemo_best_10gb", thirty: "linemo_bestv_30gb" } },
  { brand: "povo", type: "オンライン", plans: { small: "povo_3gb_30d", thirty: "povo_30gb_30d" } },
  { brand: "IIJmio", type: "格安SIM", plans: { small: "iij_5gb", medium: "iij_10gb", twenty: "iij_15gb", thirty: ["iij_25gb", "iij_35gb", "iij_45gb"], large: "iij_55gb" } },
  { brand: "日本通信SIM", type: "格安SIM", plans: { twenty: "nihon_minna_20gb", large: "nihon_50gb" } },
  { brand: "mineo", type: "格安SIM", plans: { small: "mineo_mypita_3gb", medium: "mineo_mypita_7gb", twenty: "mineo_mypita_15gb", thirty: "mineo_mypita_30gb", large: "mineo_mypita_50gb" } },
] as const;

const featuredArticleIds = ["iphone-18-pro-mnp", "sim-only-mnp-campaigns", "return-program", "ahamo-guide", "iijmio-guide", "iijmio-iphone-no-return", "rakuten-guide", "are-you-overpaying", "which-plan-is-cheapest"];
const featuredArticles = featuredArticleIds
  .map((id) => articles.find((article) => article.id === id))
  .filter((article): article is (typeof articles)[number] => Boolean(article));

const capacityLabel = (plan: MobilePlan) => plan.gb === null ? "無制限" : `${plan.gb}GB`;

export default function Home() {
 return <Layout><div className="editorial-home">
 <section className="ed-hero ed-wrap">
  <div className="ed-eyebrow">暮らしに合う、スマホの選び方。</div>
  <div className="ed-hero-grid"><div>
   <h1>スマホ代に、<br />納得できる<span>選択を。</span></h1>
   <p className="ed-lead">安さだけでは決められない。<br />料金も、買い替えも、あなたの使い方から整理します。</p>
   <div className="ed-actions"><Link className="ed-button" to="/diagnosis">自分に合う料金を診断する <span aria-hidden="true">↗</span></Link><Link className="ed-text-link" to="/bill-check">まずは請求書をチェック <span aria-hidden="true">→</span></Link></div>
   <p className="ed-small">診断は3つの質問から。個人情報の入力は不要です。</p>
  </div><div className="ed-hero-character"><img src={GUIDE_CHARACTER.src} alt={GUIDE_CHARACTER.alt} width={GUIDE_CHARACTER.width} height={GUIDE_CHARACTER.height} fetchPriority="high" /></div></div>
  <div className="ed-hero-foot"><span>料金・端末・修理・乗り換えを、同じ目線で。</span><Link to="/policy">編集方針を読む ↗</Link></div>
 </section>
 <section className="ed-bill-section ed-wrap"><div className="ed-bill-intro"><span className="ed-eyebrow">スマホ代の見直し、まずここから</span><h2><span>請求書は、</span><span>4つに分けると分かりやすい。</span></h2><p>通信料と端末代を分けて、何にいくら払っているか確認しましょう。</p></div><div className="ed-bill-items">{billChecks.map(b=><div key={b.no}><span className="ed-bill-index">{b.no}</span><div><h3>{b.title}</h3><p>{b.desc}</p></div></div>)}</div><div className="ed-bill-cta"><p>分かる項目だけで大丈夫です。</p><Link to="/bill-check" className="ed-button">請求書をチェックする <span aria-hidden="true">→</span></Link></div></section>
 <section className="ed-worries ed-wrap"><div className="ed-section-head"><div><span className="ed-eyebrow">01 / START HERE</span><h2>今、気になっていることから。</h2></div><p>見直す場所は、人それぞれ。<br />あなたの迷いに合う入口へ。</p></div>
 <div className="ed-worry-grid">{worries.map((w,i)=><Link key={w.to} to={w.to} className="ed-worry"><span className="ed-number">0{i+1}</span><span className="ed-worry-icon" aria-hidden="true">{[<Wallet key="wallet" />, <Smartphone key="phone" />, <Wrench key="repair" />, <RefreshCw key="switch" />][i]}</span><h3>{w.title}</h3><p>{w.desc}</p><span className="ed-worry-arrow" aria-hidden="true">↗</span></Link>)}</div></section>
 <section className="ed-diagnosis"><div className="ed-wrap ed-diagnosis-grid"><div><span className="ed-eyebrow">02 / FIND YOUR FIT</span><h2>「安い」より、<br />「自分に合う」を見つけよう。</h2><p>データ量、通話、店舗サポート。<br />大切にしたい条件をそろえて、候補を比べます。</p><Link to="/diagnosis" className="ed-button ed-button-light">3つの質問で料金診断 <span aria-hidden="true">↗</span></Link></div><ol className="ed-steps">{['どれくらいギガを使う？','電話は、どれくらいかける？','お店で相談したい？'].map((s,i)=><li key={s}><span>0{i+1}</span><strong>{s}</strong><span aria-hidden="true">→</span></li>)}</ol></div></section>
 <div className="ed-comparison">      {/* Carrier compare preview */}
      <section id="carrier-compare" className="bg-background-200 border-y border-background-300">
        <div className="mx-auto max-w-[1200px] px-4 md:px-6 py-16 md:py-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div className="max-w-2xl">
              <div className="text-xs text-secondary-600 font-semibold tracking-widest">通信会社比較</div>
              <h2 className="mt-2 text-2xl md:text-3xl font-bold text-foreground-900">使うギガ数から、各社の料金を比較</h2>
              <p className="mt-3 text-sm md:text-base text-foreground-600 leading-7">
                自分が使うデータ量の列を縦に見ると、同じ容量帯の主な料金プランをまとめて比較できます。
              </p>
            </div>
            <Link to="/compare" className="inline-flex items-center gap-1 text-sm font-semibold text-primary-500 hover:text-primary-600 cursor-pointer whitespace-nowrap">
              条件をそろえて比較する <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="mt-6 flex flex-wrap gap-2 text-[11px] text-foreground-700">
            <span className="rounded-full border border-background-300 bg-background-50 px-3 py-1.5">割引前の通常月額（使用量で変わる段階料金を含む）</span>
            <span className="rounded-full border border-background-300 bg-background-50 px-3 py-1.5">音声SIM・1回線</span>
            <span className="rounded-full border border-background-300 bg-background-50 px-3 py-1.5">端末代・追加オプションを除く</span>
          </div>

          <p className="mt-5 mb-2 text-xs text-foreground-500 md:hidden">表は横にスクロールできます</p>

          <div className="overflow-x-auto border-y border-background-300 bg-background-50">
            <table className="min-w-[1120px] w-full table-fixed text-base">
              <thead>
                <tr className="border-b-2 border-background-300 bg-background-100 text-left text-foreground-600">
                  <th className="w-[190px] px-4 py-4 font-semibold">通信会社</th>
                  {comparisonCapacityColumns.map((column) => (
                    <th key={column.id} className="px-3 py-4 text-center align-top">
                      <div className="text-[17px] font-bold text-foreground-900">{column.label}</div>
                      <div className="mt-1 text-xs font-normal text-foreground-500">{column.note}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonMatrix.map((row) => {
                  const brandColor = carrierBrandColors[row.brand] || "#64748B";
                  return (
                    <tr key={row.brand} className="border-b border-background-200 last:border-b-0">
                      <th className="px-4 py-4 text-left align-middle">
                        <div className="flex items-center gap-2.5">
                          <span className="h-8 w-[3px] shrink-0 rounded-full" style={{ backgroundColor: brandColor }} aria-hidden="true"></span>
                          <div>
                            <div className="text-[17px] font-bold text-foreground-900">{row.brand}</div>
                            <div className="mt-0.5 text-[11px] font-normal text-foreground-500">{row.type}</div>
                          </div>
                        </div>
                      </th>
                      {comparisonCapacityColumns.map((column) => {
                        const planValue = row.plans[column.id as keyof typeof row.plans];
                        const planIds = planValue ? (Array.isArray(planValue) ? planValue : [planValue]) : [];
                        const plans = planIds.map((planId) => planById(planId)).filter((plan): plan is MobilePlan => Boolean(plan));
                        return (
                          <td key={column.id} className="border-l border-background-200 px-3 py-4 text-center align-middle">
                            {plans.length ? (
                              <div className="divide-y divide-background-200">
                                {plans.map((plan) => (
                                  <a
                                    key={plan.id}
                                    href={plan.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    title={`${plan.name}の公式料金ページを開く`}
                                    className="group/plan block py-2 first:pt-0 last:pb-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                                  >
                                    <div className="whitespace-nowrap text-[19px] font-bold tabular-nums text-foreground-900">
                                      {plan.base.toLocaleString()}<span className="ml-0.5 text-xs font-semibold">円</span>
                                    </div>
                                    <div className="mt-0.5 text-[13px] font-semibold" style={{ color: brandColor }}>
                                      {capacityLabel(plan)}{plan.includedMinutes ? `・${plan.includedMinutes}分通話込` : ""}
                                    </div>
                                    <div className="mx-auto mt-1 max-w-[140px] truncate text-[11px] text-foreground-500 underline-offset-2 group-hover/plan:text-primary-600 group-hover/plan:underline">
                                      {plan.name} <span aria-hidden="true">↗</span>
                                    </div>
                                  </a>
                                ))}
                              </div>
                            ) : (
                              <span className="text-foreground-300">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs leading-6 text-foreground-600">
            ※ プラン名を押すと各社の公式料金ページが開きます。キャンペーン増量中のプランは現在利用できる容量を表示しています。ahamoの増量は11月30日までで、12月からの改定が予告されています。今後の条件は公式ページで確認してください。家族割・光回線セット割などを適用しない通常月額です。確認日：2026年9月20日
          </p>
          <div className="mt-7 flex justify-center">
            <Link
              to="/compare"
              className="inline-flex min-h-12 w-full max-w-md items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-base font-bold text-white shadow-sm transition-colors hover:bg-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            >
              全プランを詳しく比較する <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>

</div>
 <section className="ed-articles ed-wrap"><div className="ed-section-head"><div><span className="ed-eyebrow">03 / JOURNAL</span><h2>選ぶ前に、知っておきたいこと。</h2></div><Link to="/articles" className="ed-text-link">すべての記事を見る →</Link></div>
 <div className="ed-journal-grid">{featuredArticles.map((a,i)=><Link to={'/articles/'+a.id} key={a.id} className={'ed-story '+(i===0?'ed-story-featured':'')}><div className="ed-story-image"><img src={a.image} alt="" loading="lazy" /></div><div className="ed-story-copy"><span className="ed-eyebrow">{a.category}</span><h3>{a.title}</h3>{i===0&&<p>{a.excerpt}</p>}<div className="ed-story-meta">確認日 {a.date}<span>読了 {a.readTime} ↗</span></div></div></Link>)}</div></section>
 <section className="ed-about ed-wrap"><img src={GUIDE_CHARACTER.src} alt={GUIDE_CHARACTER.alt} width="160" height="200" loading="lazy"/><div><span className="ed-eyebrow">OUR APPROACH</span><h2>むずかしい料金を、<br />判断できる形に。</h2><p>公式情報をもとに、料金と条件を分かりやすく整理。特定の会社を一律にすすめず、使い方に合う選択肢を比べられる案内所を目指しています。</p><Link to="/about" className="ed-text-link">この案内所について →</Link></div></section>
 </div></Layout>;
}
